import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'
import axios, { AxiosResponse } from 'axios'
import { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { quoteLineType, quoteType, salesforceOpportunityType, vendorQuoteType } from '../types'
import { CRMSearch, CurrencyInput, DatePicker } from '../helper'
import { coreCompetencies, salesforceForecastCategories, salesforceStages, setAsides } from '../lists'
import dayjs from 'dayjs'
import { dataContext } from '..'
import { Clear } from '@mui/icons-material'
import './QuoteDetailsOptionsDialogs.css'

export function NewVersionDialog(props: {
  open: boolean
  quoteId: string
  nextVersion: number
  onClose: () => void
  contactId?: string
}) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [error, setError] = useState(false)

  const handleNewVersion = () => {
    setLoading(true)
    setError(false)
    axios
      .post(`/newVersion/${props.quoteId}`, { contactId: props.contactId })
      .then(res => {
        props.onClose()
        setOpenSnackbar(true)
        navigate(`/quote/${res.data}`)
      })
      .catch(err => {
        setError(true)
        console.error(err)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <>
      <Dialog open={props.open} onClose={props.onClose} fullWidth>
        <DialogTitle>Create New Version?</DialogTitle>
        <DialogContent>
          <Typography>You are about to create a new version of this quote.</Typography>
          <Typography>Version #: {props.nextVersion}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={props.onClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleNewVersion} variant="contained" loading={loading}>
            Create New Version
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        autoHideDuration={5000}
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        message="New version created successfully"
      />
      <Snackbar open={error}>
        <Alert severity="error" variant="filled" onClose={() => setError(false)}>
          Failed to create new version. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
    </>
  )
}

export function CloneDialog(props: { open: boolean; quoteId: string; contactId?: string; onClose: () => void }) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [error, setError] = useState(false)

  const handleClone = () => {
    setLoading(true)
    setError(false)
    axios
      .post(`/cloneQuote/${props.quoteId}`, { contactId: props.contactId })
      .then(res => {
        props.onClose()
        setOpenSnackbar(true)
        navigate(`/quote/${res.data}`)
      })
      .catch(err => {
        setError(true)
        console.error(err)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <>
      <Dialog open={props.open} onClose={props.onClose} fullWidth>
        <DialogTitle>Clone</DialogTitle>
        <DialogContent>
          <Typography>
            You are about to create a clone copy of this quote. Are you sure you would like to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={props.onClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleClone} variant="contained" loading={loading}>
            Yes, Proceed to Clone
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        autoHideDuration={5000}
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        message="Quote cloned successfully"
      />
      <Snackbar open={error}>
        <Alert severity="error" variant="filled" onClose={() => setError(false)}>
          Failed to clone quote. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
    </>
  )
}

export function DeleteDialog(props: { open: boolean; quoteId: string; onClose: () => void }) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [error, setError] = useState(false)

  const handleDelete = () => {
    setLoading(true)
    setError(false)
    axios
      .post(`/deleteQuote/${props.quoteId}`)
      .then(res => {
        props.onClose()
        setOpenSnackbar(true)
        if (res.data) {
          navigate(`/quote/${res.data}`)
        } else {
          navigate(`/quotes`, { state: { deleted: true } })
        }
      })
      .catch(err => {
        setError(true)
        console.error(err)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <>
      <Dialog open={props.open} onClose={props.onClose} fullWidth>
        <DialogTitle>Delete Quote?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this quote? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={props.onClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" loading={loading}>
            Delete Quote
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        autoHideDuration={5000}
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        message="Quote deleted successfully"
      />
      <Snackbar open={error}>
        <Alert severity="error" variant="filled" onClose={() => setError(false)}>
          Failed to delete quote. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
    </>
  )
}

export function SyncQuoteDialog(props: {
  open: boolean
  onClose: () => void
  handleSync: () => Promise<AxiosResponse<salesforceOpportunityType>>
}) {
  const [loading, setLoading] = useState(false)
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [error, setError] = useState(false)

  const handleSync = () => {
    setLoading(true)
    setError(false)
    props
      .handleSync()
      .then(() => {
        props.onClose()
        setOpenSnackbar(true)
      })
      .catch(err => {
        setError(true)
        console.error(err)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <>
      <Dialog open={props.open} onClose={props.onClose} fullWidth>
        <DialogTitle>Sync Quote with Salesforce?</DialogTitle>
        <DialogContent>
          <Typography>You are about to update the Salesforce quote with the latest data.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={props.onClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleSync} variant="contained" loading={loading}>
            Sync Salesforce Quote
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        autoHideDuration={5000}
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        message="Quote synced successfully"
      />
      <Snackbar open={error}>
        <Alert severity="error" variant="filled" onClose={() => setError(false)}>
          Failed to sync quote with Salesforce. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
    </>
  )
}

export function NewOpportunityDialog(props: {
  open: boolean
  quoteId: string
  onClose: () => void
  setCrmOpportunity: (opportunity: salesforceOpportunityType) => void
  setAccount: (account: { id: string; label: string } | null) => void
  setContact: (contact: { id: string; label: string } | null) => void
  setOpportunity: (opportunity: { id: string; label: string } | null) => void
}) {
  const { user } = useContext(dataContext)

  const [loading, setLoading] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState<{ id: string; label: string } | null>(null)
  const [selectedContact, setSelectedContact] = useState<{ id: string; label: string } | null>(null)
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [opportunity, setOpportunity] = useState<Partial<salesforceOpportunityType>>({
    Name: '',
    CloseDate: null,
    StageName: '',
    ForecastCategoryName: '',
    Line_Of_Business__c: [],
    Set_Asides__c: [],
  })
  const [missingFields, setMissingFields] = useState({
    Name: false,
    CloseDate: false,
    StageName: false,
    ForecastCategoryName: false,
    Line_Of_Business__c: false,
    Set_Asides__c: false,
    Account: false,
    Contact: false,
  })
  const [createError, setCreateError] = useState(false)

  const handleClose = () => {
    setOpportunity({
      Name: '',
      CloseDate: null,
      StageName: '',
      ForecastCategoryName: '',
      Line_Of_Business__c: [],
      Set_Asides__c: [],
    })
    setMissingFields({
      Name: false,
      CloseDate: false,
      StageName: false,
      ForecastCategoryName: false,
      Line_Of_Business__c: false,
      Set_Asides__c: false,
      Account: false,
      Contact: false,
    })
    setSelectedAccount(null)
    setSelectedContact(null)
    props.onClose()
  }

  const handleCreateOpportunity = () => {
    const missingFieldsObj = {
      Name: !opportunity.Name || opportunity.Name.trim() === '',
      CloseDate: !opportunity.CloseDate,
      StageName: !opportunity.StageName || opportunity.StageName.trim() === '',
      ForecastCategoryName: !opportunity.ForecastCategoryName || opportunity.ForecastCategoryName.trim() === '',
      Line_Of_Business__c: !opportunity.Line_Of_Business__c || opportunity.Line_Of_Business__c.length === 0,
      Set_Asides__c: !opportunity.Set_Asides__c || opportunity.Set_Asides__c.length === 0,
      Account: !selectedAccount || selectedAccount.id.trim() === '',
      Contact: !selectedContact || selectedContact.id.trim() === '',
    }
    setMissingFields(missingFieldsObj)

    if (Object.values(missingFieldsObj).some(value => value)) {
      return
    }

    setLoading(true)
    setCreateError(false)
    axios
      .post(`/editQuote/${props.quoteId}`, {
        newOpportunity: {
          ...opportunity,
          AccountId: selectedAccount?.id,
          Owner: {
            Email: user.email ?? '',
          },
        },
      })
      .then(res => {
        props.setCrmOpportunity(res.data)
        props.setAccount(selectedAccount)
        props.setContact(selectedContact)
        props.setOpportunity({ id: res.data.Id, label: res.data.Name })
        props.onClose()
        setOpenSnackbar(true)
      })
      .catch(err => {
        setCreateError(true)
        console.error(err)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <>
      <Dialog open={props.open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>New Opportunity</DialogTitle>
        <DialogContent>
          <Typography>All fields are required.</Typography>
          <Grid container spacing={2}>
            <Grid size={6}>
              <TextField
                fullWidth
                size="small"
                label="Opportunity Name"
                value={opportunity.Name}
                onChange={e => setOpportunity(prev => ({ ...prev, Name: e.target.value }))}
                error={missingFields.Name}
                helperText={missingFields.Name ? 'Opportunity name is required' : ''}
              />
            </Grid>
            <Grid size={6}>
              <DatePicker
                fullWidth
                size="small"
                label="Close Date"
                value={opportunity.CloseDate ? dayjs(opportunity.CloseDate) : null}
                onChange={e => setOpportunity(prev => ({ ...prev, CloseDate: e ? e.toDate() : null }))}
                error={missingFields.CloseDate}
                helperText={missingFields.CloseDate ? 'Close date is required' : ''}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Stage"
                value={opportunity.StageName}
                onChange={e => setOpportunity(prev => ({ ...prev, StageName: e.target.value }))}
                error={missingFields.StageName}
                helperText={missingFields.StageName ? 'Stage is required' : ''}
              >
                {salesforceStages.map(stage => (
                  <MenuItem key={stage} value={stage}>
                    {stage}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Forecast Category"
                value={opportunity.ForecastCategoryName}
                onChange={e => setOpportunity(prev => ({ ...prev, ForecastCategoryName: e.target.value }))}
                error={missingFields.ForecastCategoryName}
                helperText={missingFields.ForecastCategoryName ? 'Forecast category is required' : ''}
              >
                {salesforceForecastCategories.map(category => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Core Competency"
                slotProps={{ select: { multiple: true } }}
                value={opportunity.Line_Of_Business__c}
                onChange={e => setOpportunity(prev => ({ ...prev, Line_Of_Business__c: e.target.value }))}
                error={missingFields.Line_Of_Business__c}
                helperText={missingFields.Line_Of_Business__c ? 'Core competency is required' : ''}
              >
                {coreCompetencies.map(competency => (
                  <MenuItem key={competency} value={competency}>
                    {competency}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Set Asides"
                slotProps={{ select: { multiple: true } }}
                value={opportunity.Set_Asides__c}
                onChange={e => setOpportunity(prev => ({ ...prev, Set_Asides__c: e.target.value }))}
                error={missingFields.Set_Asides__c}
                helperText={missingFields.Set_Asides__c ? 'Set aside is required' : ''}
              >
                {setAsides.map(aside => (
                  <MenuItem key={aside} value={aside}>
                    {aside}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={6}>
              <CRMSearch
                object="Account"
                value={selectedAccount}
                label="Account"
                onChange={(e, value) => setSelectedAccount(value)}
                error={missingFields.Account}
                errorText={missingFields.Account ? 'Account is required' : ''}
              />
            </Grid>
            <Grid size={6}>
              <CRMSearch
                object="Contact"
                accountId={selectedAccount?.id}
                value={selectedContact}
                label="Contact"
                onChange={(e, value) => setSelectedContact(value)}
                error={missingFields.Contact}
                errorText={missingFields.Contact ? 'Contact is required' : ''}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateOpportunity} loading={loading}>
            Create New Opportunity
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        autoHideDuration={5000}
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        message="Opportunity created successfully"
      />
      <Snackbar open={createError}>
        <Alert severity="error" variant="filled" onClose={() => setCreateError(false)}>
          Failed to create opportunity. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
    </>
  )
}

export function UploadVendorTemplateDialog(props: {
  open: boolean
  quoteId: string
  onClose: () => void
  setVendorQuotes: React.Dispatch<React.SetStateAction<vendorQuoteType[]>>
  setLineItems: React.Dispatch<React.SetStateAction<(quoteLineType & { children: quoteLineType[] })[]>>
  setQuote: React.Dispatch<React.SetStateAction<quoteType>>
}) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [headerInfo, setHeaderInfo] = useState({
    VendorName: '',
    VendorQuoteNumber: '',
    ExpirationDate: null as Date | null,
    TotalAmount: null as number | null,
  })
  const [loading, setLoading] = useState(false)
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [missingFile, setMissingFile] = useState(false)
  const [uploadError, setUploadError] = useState(false)

  const handleClose = () => {
    props.onClose()
    setSelectedFile(null)
    setHeaderInfo({
      VendorName: '',
      VendorQuoteNumber: '',
      ExpirationDate: null,
      TotalAmount: null,
    })
    setMissingFile(false)
  }

  const handleUpload = () => {
    if (!selectedFile) {
      setMissingFile(true)
      return
    }

    setLoading(true)
    setMissingFile(false)

    const formData = new FormData()
    formData.append('file', selectedFile as Blob)
    formData.append('header', new Blob([JSON.stringify(headerInfo)], { type: 'application/json' }))
    axios
      .post(`/uploadVendorTemplate/${props.quoteId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(res => {
        handleClose()
        setOpenSnackbar(true)
        props.setVendorQuotes(prev => [
          ...prev,
          { ...headerInfo, VendorQuoteId: headerInfo.VendorQuoteNumber, QuoteId: props.quoteId },
        ])
        props.setLineItems(prev => [
          ...prev,
          ...res.data.lineItems.map((lineItem: quoteLineType) => ({ ...lineItem, children: [] })),
        ])
        props.setQuote(prev => ({ ...prev, ...res.data.totals }))
      })
      .catch(err => {
        console.error(err)
        setUploadError(true)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <>
      <Dialog open={props.open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>New Vendor Template</DialogTitle>
        <DialogContent className="vendor-template-upload-dialog">
          <div className="file-input">
            <Button component="label" variant="contained" size="small">
              Select File
              <input
                type="file"
                accept=".xlsx,.xls"
                value={selectedFile ? undefined : ''}
                onChange={e => setSelectedFile(e.target.files?.[0] ?? null)}
                hidden
              />
            </Button>
            {missingFile && !selectedFile && <Typography color="error">File is required</Typography>}
            <div className="file-input-name">
              {selectedFile && (
                <IconButton size="small" onClick={() => setSelectedFile(null)}>
                  <Clear />
                </IconButton>
              )}
              <Typography>{selectedFile?.name}</Typography>
            </div>
          </div>
          <Grid container spacing="16px">
            <Grid size={6}>
              <TextField
                fullWidth
                size="small"
                label="Vendor Name"
                value={headerInfo.VendorName}
                onChange={e => setHeaderInfo(prev => ({ ...prev, VendorName: e.target.value }))}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                size="small"
                label="Vendor Quote Number"
                value={headerInfo.VendorQuoteNumber}
                onChange={e => setHeaderInfo(prev => ({ ...prev, VendorQuoteNumber: e.target.value }))}
              />
            </Grid>
            <Grid size={6}>
              <DatePicker
                fullWidth
                size="small"
                label="Expiration Date"
                value={headerInfo.ExpirationDate ? dayjs(headerInfo.ExpirationDate) : null}
                onChange={e => setHeaderInfo(prev => ({ ...prev, ExpirationDate: e ? e.toDate() : null }))}
              />
            </Grid>
            <Grid size={6}>
              <CurrencyInput
                fullWidth
                size="small"
                label="Total Amount"
                value={headerInfo.TotalAmount}
                onChange={e => setHeaderInfo(prev => ({ ...prev, TotalAmount: e }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleUpload} loading={loading}>
            Upload Vendor Template
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        autoHideDuration={5000}
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        message="Vendor template uploaded successfully"
      />
      <Snackbar open={uploadError}>
        <Alert severity="error" variant="filled" onClose={() => setUploadError(false)}>
          Failed to upload vendor template. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
    </>
  )
}

export function GenerateQuoteDialog(props: { open: boolean; onClose: () => void; quoteId: string; quoteName: string }) {
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [error, setError] = useState(false)
  const [template, setTemplate] = useState('')
  const [fileType, setFileType] = useState('')
  const [missingFields, setMissingFields] = useState(false)

  const handleGenerate = () => {
    if (!template || !fileType) {
      setMissingFields(true)
      return
    }
    axios
      .post(`/generateQuote/${props.quoteId}`, { template, fileType }, { responseType: 'blob' })
      .then(res => {
        const url = window.URL.createObjectURL(res.data)
        const a = document.createElement('a')
        a.href = url
        a.download = `${props.quoteName}.${fileType}`
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
        props.onClose()
      })
      .catch(err => {
        setError(true)
        console.error(err)
      })
  }

  return (
    <>
      <Dialog
        open={props.open}
        onClose={() => props.onClose()}
        fullWidth
        className="generate-quote-dialog"
        maxWidth="md"
      >
        <DialogTitle>Generate Quote</DialogTitle>
        <DialogContent>
          <Typography>All fields are required.</Typography>
          <Typography variant="h6">Template</Typography>
          <RadioGroup value={template} onChange={e => setTemplate(e.target.value)}>
            <FormControlLabel value="GSA/2GIT" control={<Radio />} label="GSA/2GIT" />
            <FormControlLabel value="SEWP-V B (VIT)" control={<Radio />} label="SEWP-V B (VIT)" />
            <FormControlLabel value="SEWP-V C (IMPRES)" control={<Radio />} label="SEWP-V C (IMPRES)" />
            <FormControlLabel value="DLA" control={<Radio />} label="DLA" />
            <FormControlLabel value="Open Market" control={<Radio />} label="Open Market" />
            <FormControlLabel value="Open Market - Commercial" control={<Radio />} label="Open Market - Commercial" />
            <FormControlLabel value="ITES-4H" control={<Radio />} label="ITES-4H" />
            <FormControlLabel value="MDA SHIELD" control={<Radio />} label="MDA SHIELD" />
            <FormControlLabel value="SeaPort NxG" control={<Radio />} label="SeaPort NxG" />
          </RadioGroup>
          <Typography variant="h6">File Type</Typography>
          <RadioGroup value={fileType} onChange={e => setFileType(e.target.value)}>
            <FormControlLabel value="pdf" control={<Radio />} label="PDF" />
            <FormControlLabel value="xlsx" control={<Radio />} label="Excel" />
          </RadioGroup>
          <DialogActions>
            <Button onClick={props.onClose} variant="outlined">
              Cancel
            </Button>
            <Button onClick={handleGenerate} variant="contained">
              Generate Quote
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
      <Snackbar
        autoHideDuration={5000}
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        message="Quote generated successfully"
      />
      <Snackbar open={missingFields}>
        <Alert severity="info" variant="filled" onClose={() => setMissingFields(false)}>
          Please select a template and file type.
        </Alert>
      </Snackbar>
      <Snackbar open={error}>
        <Alert severity="error" variant="filled" onClose={() => setError(false)}>
          Failed to generate quote. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
    </>
  )
}
